<template lang="pug">
v-main
  v-container(fluid)
    v-flex
      v-card
        v-toolbar.elevation-0(color="white")
          v-toolbar-title Chart of Accounts (COA)
          v-spacer
          v-btn(dark, color="blue-secondary", to="/coa/create" class="elevation-0") CREATE
          v-btn.ml-2(dark, color="primary", to="/coa/bulk-upload" class="elevation-0") BULK UPLOAD
        v-divider
        v-toolbar.elevation-0.mt-2.mb-4(color="white")
          v-select.hidden-sm-and-down.col-3.pl-0(
            label="Business Unit",
            v-model="selectedBU",
            :items="filteredBUList",
            item-text="business_unit.bu_name",
            item-value="business_unit.id",
            clearable,
            hide-details,
            @change="onBUChange"
          )
            template(v-slot:item="data")
              v-list-item-title {{ getBUName(data.item) }}
            template(v-slot:selection="data")
              span {{ getBUName(data.item) }}
          span.pr-2
          v-select.hidden-sm-and-down.col-2.pl-0(
            multiple
            label="Status"
            hide-details
            v-model="selectedStatus"
            :items="statusOptions"
            item-value="value"
            item-text="text"
            @change="onStatusChange"
          )
            template(v-slot:prepend-item)
              v-list-item(ripple @mousedown.prevent @click="toggleStatus")
                v-list-item-action
                  v-icon(:color="selectedStatus.length > 0 ? 'indigo darken-4' : ''") {{ iconXStatus }}
                v-list-item-content
                  v-list-item-title Select All
              v-divider(class="mt-2")
            template(v-slot:selection="{ item, index }")
              v-chip(
                v-if="selectedStatus.length == 1"
                close
                @click:close="removeStatus(item.value)"
              )
                span {{ item.text }}
              v-chip(
                v-else-if="index===1"
                close
                @click:close="removeAllStatus"
              )
                span {{ selectedStatus.length }} Status
          span.pr-2
          v-text-field.hidden-sm-and-down.col-5(
            flat,
            append-icon="search",
            placeholder="Search by COA Code, Description, or Account Type",
            v-model="keyword",
            hide-details,
            @keyup.enter="loadData"
          )
        v-divider
        v-card-text.pa-0
          v-data-table(
            :headers="dataHeaders",
            :items="dataItems",
            :loading="loading",
            :server-items-length="totalItems",
            :items-per-page="itemsPerPage",
            :page="page",
            @update:options="onPaginationChange"
          )
            template(v-slot:item.bu="{ item }")
              span {{ item.bu.name }}
            
            template(v-slot:item.status="{ item }")
              v-chip(:color="item.status ? 'success' : 'default'", small) {{ item.status ? 'Active' : 'Non Active' }}
            
            template(v-slot:item.updated_at="{ item }")
              span {{ formatDate(item.updated_at) }}

            template(v-slot:item.data-table-expand="{ item }")
              v-icon.mr-2(title="edit", color="green", @click="onEdit(item._id)")
                | edit

            template(v-slot:no-data)
              div.pa-15 
                img(src="../assets/icon/empty-users.png")
                div(class="empty__title mt-1") Data not found
                div(class="empty__subtitle") Data is empty or Try adjusting your filters.
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "COAList",
  data() {
    return {
      keyword: "",
      selectedBU: "",
      selectedStatus: [],
      buList: [],
      statusOptions: [
        { text: "Active", value: true },
        { text: "Non Active", value: false },
      ],
      dataHeaders: [
        { text: "BU", value: "bu.name", sortable: true },
        { text: "COA Code", value: "code", sortable: true },
        { text: "Description", value: "description", sortable: true },
        { text: "Account Type", value: "account_type", sortable: true },
        { text: "Status", value: "status", sortable: true },
        { text: "Updated At", value: "updated_at", sortable: true },
        { text: "Actions", value: "data-table-expand", sortable: false },
      ],
      dataItems: [],
      loading: false,
      totalItems: 0,
      itemsPerPage: 10,
      page: 1,
      sortBy: "updated_at",
      sortOrder: "desc",
    };
  },
  computed: {
    filteredBUList() {
      return this.buList.filter(item => {
        return item.business_unit &&
               item.business_unit.is_active === true &&
               item.is_payment_voucher === true;
      });
    },
    selectAllStatus() {
      return this.selectedStatus.length === this.statusOptions.length;
    },
    selectSomeStatus() {
      return this.selectedStatus.length > 0 && !this.selectAllStatus;
    },
    iconXStatus() {
      if (this.selectAllStatus) return 'close';
      else if (this.selectSomeStatus) return 'clear_all';
      else return 'check_box_outline_blank';
    },
  },
  mounted() {
    this.loadBUList();
  },
  methods: {
    loadBUList() {
      const token = localStorage.getItem("token");
      const userData = this.$store.state.dataUsers;
      const userId = userData.user || userData;
      
      if (userId) {
        axios
          .get(`${process.env.VUE_APP_API}/users/active/bu/${userId._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            this.buList = res.data.data || [];
            this.$nextTick(() => {
              this.loadData();
            });
          })
          .catch((err) => {
            this.$toasted.error("Failed to load business units", { icon: "error" });
          });
      }
    },
    loadData() {
      this.loading = true;
      const token = localStorage.getItem("token");

      const params = {
        page: this.page,
        limit: this.itemsPerPage,
        sort: this.sortBy,
        order: this.sortOrder,
      };

      if (this.keyword) params.q = this.keyword;
      if (this.selectedStatus.length > 0) {
        params.status = this.selectedStatus.join(",");
      }

      let buQueryParam = '';
      if (this.selectedBU) {
        buQueryParam = this.selectedBU;
      } else {
        const buIds = this.filteredBUList.map(item => item.business_unit.id);
        if (buIds.length > 0) {
          buQueryParam = buIds.join(',');
        }
      }

      if (!buQueryParam) {
        this.dataItems = [];
        this.totalItems = 0;
        this.loading = false;
        return;
      }

      params.bu = buQueryParam;

      axios
        .get(`${process.env.VUE_APP_API}/coa`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        })
        .then((res) => {
          this.dataItems = res.data.data || [];
          this.totalItems = res.data.pagination && res.data.pagination.total || 0;
        })
        .catch((err) => {
          this.$toasted.error("Failed to load data", { icon: "error" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onBUChange() {
      this.page = 1;
      this.loadData();
    },
    onStatusChange() {
      this.page = 1;
      this.loadData();
    },
    toggleStatus() {
      this.$nextTick(() => {
        if (this.selectAllStatus) {
          this.selectedStatus = [];
        } else {
          this.selectedStatus = this.statusOptions.map(s => s.value);
        }
        this.onStatusChange();
      });
    },
    removeStatus(value) {
      const idx = this.selectedStatus.indexOf(value);
      if (idx > -1) {
        this.selectedStatus.splice(idx, 1);
        this.selectedStatus = [...this.selectedStatus];
        this.onStatusChange();
      }
    },
    removeAllStatus() {
      this.selectedStatus = [];
      this.onStatusChange();
    },
    onPaginationChange(options) {
      this.page = options.page;
      this.itemsPerPage = options.itemsPerPage;
      
      if (options.sortBy && options.sortBy.length > 0) {
        this.sortBy = options.sortBy[0];
        this.sortOrder = options.sortDesc[0] ? "desc" : "asc";
      }
      
      this.loadData();
    },
    onEdit(id) {
      this.$router.push({
        name: "COA Edit",
        params: { id },
      });
    },
    formatDate(date) {
      return moment(date).format("DD MMM YYYY HH:mm");
    },
    getBUName(item) {
      return item.business_unit && item.business_unit.bu_name ? item.business_unit.bu_name : '';
    },
  },
};
</script>

<style scoped>
.empty__title {
  font-size: 18px;
  font-weight: 600;
}
.empty__subtitle {
  color: #999;
  font-size: 14px;
}
</style>
